import { IMaterial } from "../types";

/**
 * ⚠️ WORKAROUND: Validator for Dialog (Modal) Question Content
 *
 * WHY THIS EXISTS:
 * Part of the modal questions workaround. Validates Bill of Materials data that's
 * collected inside a modal dialog. This validator ensures all required fields in
 * the modal are filled before allowing the user to proceed to the next page.
 *
 * WHAT IT VALIDATES:
 * - Composition array exists and has valid fibre percentages
 * - Total percentage equals exactly 100%
 * - Required units are specified (fabric usage unit, waste unit)
 * - Material type is selected
 * - Fabric usage and waste amounts are provided
 * - Fabric supplier name is filled
 * - Yarn suppliers (if any) have composition specified
 *
 * RELATED FILES:
 * @see src/models/question-dialog-model.ts - Dialog question model
 * @see src/components/survey/questions/custom/dialog/survey-question-dialog.tsx - Modal UI
 * @see src/inner-schemas/bom-extra-info.js - Schema for modal questions
 * @see docs/workarounds/README.md#modal-questions-hack-dialog-element - Full documentation
 *
 * DO NOT remove or simplify this validator without understanding the modal questions workaround!
 */
export function validateDialogBomContent(
  params?: Array<Omit<IMaterial, "position"> | undefined>
) {
  if (!params || !Array.isArray(params)) {
    return false;
  }

  // Since we're validating a single material's content, we'll look at the first item
  const material = params[0];
  if (!material) {
    return false;
  }

  // Validate composition array and its total percentage
  const hasValidComposition =
    Array.isArray(material.composition) &&
    material.composition.length > 0 &&
    material.composition.every(
      (item) =>
        item.percentage !== undefined &&
        item.percentage > 0 &&
        item.fibre !== undefined &&
        item.fibre !== ""
    );

  // Calculate total percentage
  const totalPercentage =
    material.composition?.reduce(
      (sum, item) => sum + (item.percentage || 0),
      0
    ) || 0;

  // Validate required units
  const hasRequiredUnits =
    material.fabricUsageUnit !== undefined &&
    material.fabricUsageUnit !== "" &&
    material.fabricWasteUnit !== undefined &&
    material.fabricWasteUnit !== "";

  const hasMaterialType =
    material.materialType !== undefined && material.materialType !== "";

  const hasFabricUsage =
    material.fabricUsage !== undefined && material.fabricUsage !== 0;

  const hasFabricWaste =
    material.fabricWaste !== undefined && material.fabricWaste !== 0;

  const hasFabricSupplier =
    material.fabricSupplierName !== undefined &&
    material.fabricSupplierName !== "";

  // Validate yarn suppliers
  let hasValidYarnSuppliers = true;

  if (
    Array.isArray(material.yarnSuppliers) &&
    material.yarnSuppliers.length > 0
  ) {
    hasValidYarnSuppliers = material.yarnSuppliers
      .filter((supplier) => supplier.name !== undefined && supplier.name !== "")
      .every(
        (supplier) =>
          supplier.composition !== undefined && supplier.composition !== ""
      );
  }

  const isDialogValid =
    hasValidComposition &&
    totalPercentage === 100 &&
    hasRequiredUnits &&
    hasValidYarnSuppliers &&
    hasMaterialType &&
    hasFabricUsage &&
    hasFabricWaste &&
    hasFabricSupplier;

  return isDialogValid;
}
